import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Alert, Box, Stack, Typography, Button, CircularProgress } from '@mui/material';

import SegmentDetails from '../icp/SegmentDetails';
import PersonaICPExtended from '../icp/PersonaICPExtended';
import PersonaB2CExtended from '../icp/PersonaB2CExtended';
// Redux actions are now handled in the service
import { createIcp } from '../../services/newIcpService';
import { createIcpSuccess } from '../../store/reducers/actions';
import BlockedInput from '../askPage/BlockedInput';

function areSegmentsEqual(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  return a.every((seg, idx) => seg.segment_id === b[idx]?.segment_id);
}

const ICPResultAdapter = ({
  segments = []
  // selectedSegment = null,  <- No longer need this prop
  //   taskId
}) => {
  const dispatch = useDispatch();

  // Directly use the selectedSegment from Redux state
  const selectedSegment = useSelector((state) => state.icpReducer?.selectedSegment);
  const generatedICPs = useSelector((state) => state.icpReducer?.generatedICP);
  const error = useSelector((state) => state.icpReducer?.error);
  const icpTaskId = useSelector((state) => state.icpReducer?.task_id);
  const reduxSegments = useSelector((state) => state.icpReducer?.segments || []);
  const customerTypes = useSelector((state) => state.icpReducer?.customerTypes || {});

  // Set up Redux state for SegmentDetails component
  useEffect(() => {
    if (segments.length > 0 && !areSegmentsEqual(segments, reduxSegments)) {
      // Set segments in Redux
      dispatch({
        type: 'SET_ICP_SEGMENTS',
        payload: {
          segments: segments,
          task_id: icpTaskId
        }
      });
      // Set selected segment in Redux if not already set
      if (!selectedSegment) {
        dispatch({ type: 'SET_SELECTED_SEGMENT', payload: segments[0] });
      }
    }
  }, [segments, reduxSegments, selectedSegment, icpTaskId, dispatch]);

  const handleGenerateICP = async () => {
    console.log('icpTaskId', icpTaskId);
    if (!selectedSegment || !icpTaskId) return;

    // Set the segment's ICP to null to trigger the loader immediately
    dispatch(
      createIcpSuccess({
        segmentId: selectedSegment.segment_id,
        icp: null
      })
    );

    try {
      // Service function now handles Redux updates automatically
      await createIcp(icpTaskId, selectedSegment.segment_id);
    } catch (error) {
      // Error handling is now done in the service
      console.error('ICP creation failed:', error);
    }
  };

  // Get ICP based on the selected segment from Redux
  const generatedICP = selectedSegment && generatedICPs ? generatedICPs[selectedSegment.segment_id] : null;

  // Get customer type for the selected segment
  const customerType = selectedSegment ? customerTypes[selectedSegment.segment_id] : null;

  // Show loader if icpLoading is true OR if generatedICPs exists and the value for the selected segment is null
  const isIcpLoadingForSelectedSegment =
    !!selectedSegment &&
    generatedICPs &&
    Object.prototype.hasOwnProperty.call(generatedICPs, selectedSegment.segment_id) &&
    generatedICPs[selectedSegment.segment_id] === null;

  // Check if we're in loading state
  const isLoading = !segments || segments.length === 0;

  return (
    <Stack spacing={3}>
      {/* Header Section */}
      <Box sx={{ textAlign: 'center', px: 2, pt: 4 }}>
        <Typography variant="h2">Audience Segmentation</Typography>
        <Typography
          variant="h6"
          sx={{
            color: 'text.secondary',
            maxWidth: '800px',
            mx: 'auto',
            mt: 1
          }}
        >
          Analyze and understand your key customer segments
        </Typography>
      </Box>

      {/* Show loader next to title when loading */}
      {isLoading && <CircularProgress size={24} />}

      {/* Show segments only if they exist */}
      {!isLoading && segments && segments.length > 0 && selectedSegment && (
        <SegmentDetails /> // No need to pass props, it will get from Redux
      )}

      {/* ICP Section - only show when not loading */}
      {!isLoading && (
        <>
          <div id="icp-profile" />
          <Box sx={{ textAlign: 'center', px: 2, mb: 3, pt: 4 }}>
            <Typography variant="h2">Ideal Customer Profile</Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                maxWidth: '800px',
                mx: 'auto',
                mt: 2
              }}
            >
              Detailed profile based on selected segment analysis
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              Error generating ICP: {error}
            </Alert>
          )}

          {isIcpLoadingForSelectedSegment ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <BlockedInput text="Generating your Ideal Customer Profile..." />
            </Box>
          ) : generatedICP && selectedSegment && generatedICP.name ? (
            // Use the proper ICP templates based on customer type from Redux
            customerType === 'b2c' ? (
              <PersonaB2CExtended selectedPersona={generatedICP} detailed={true} />
            ) : customerType === 'b2b' ? (
              <PersonaICPExtended selectedPersona={generatedICP} detailed={true} />
            ) : (
              // Default to B2B template if customer type is not determined
              <PersonaICPExtended selectedPersona={generatedICP} detailed={true} />
            )
          ) : (
            selectedSegment && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 5, gap: 2 }}>
                <Button variant="gradient" onClick={handleGenerateICP}>
                  Create ICP for {selectedSegment.name}
                </Button>
              </Box>
            )
          )}
        </>
      )}
    </Stack>
  );
};

export default ICPResultAdapter;
